import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgIcon } from '@ng-icons/core';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';

import { LibSharedCommonModule } from 'projects/lib-shared-common/src/public-api';

import { ZardDropdownImports } from '../../app/shared/components/dropdown/dropdown.imports';
import { provideSolvaceIcons } from '../svz-icons/solvace-icons';

/**
 * Page sizes offered by every table in this UI: 20 / 30 / 40 / 50.
 *
 * The list starts at 20, not 10 — a shorter first page paginates screens that comfortably fit more
 * rows. Kept as the component's default so the policy lives in ONE place and no screen has to repeat
 * it; a consumer with a real reason to differ can still pass `pageSizeOptions`.
 */
const PAGINATE_OPTIONS = [20, 30, 40, 50];

const PAGE_SIZE_OPTION_CLASS =
  'justify-between gap-3 rounded-[8px] px-2.5 py-[9px] text-sm [&_svg]:size-[18px] hover:bg-[#f2f2f4] focus:bg-[#f2f2f4] focus-visible:bg-[#f2f2f4] data-highlighted:bg-[#f2f2f4]';

export interface SvzPaginateEvent {
  page: number;
  pageSize: number;
}

@Component({
  standalone: true,
  selector: 'svz-paginate',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-paginate.component.html',
  host: { class: 'block w-full' },
  imports: [NgIcon, TranslocoModule, LibSharedCommonModule, ZardDropdownImports],
  providers: [provideSolvaceIcons()],
})
export class SvzPaginateComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly activeLang = toSignal(this.transloco.langChanges$, { initialValue: this.transloco.getActiveLang() });

  /** English shows the literal "of" for the range: the pt-BR term `de` is translated as "from" by Multilingual (see template). */
  protected readonly isEnglish = computed(() => /^en(-|$)/i.test(this.activeLang() ?? ''));

  readonly totalRegisters = input(0);
  readonly current = input(1);
  /** The smallest offered size, so an unset `pageSize` always matches a real option in the select. */
  readonly pageSize = input(PAGINATE_OPTIONS[0]);
  readonly disabled = input(false);
  readonly loading = input(false);
  readonly showPageSize = input(true);
  readonly pageSizeOptions = input<number[]>(PAGINATE_OPTIONS);
  readonly testIdPrefix = input('svz-paginate');

  protected readonly optionClass = `${PAGE_SIZE_OPTION_CLASS} text-[#33373f] hover:text-[#33373f] focus:text-[#33373f] focus-visible:text-[#33373f] data-highlighted:text-[#33373f]`;
  protected readonly selectedOptionClass = `${PAGE_SIZE_OPTION_CLASS} text-primary hover:text-primary focus:text-primary focus-visible:text-primary data-highlighted:text-primary`;

  readonly onPageChanged = output<SvzPaginateEvent>();
  readonly onPageSizeChanged = output<SvzPaginateEvent>();

  protected readonly totalPages = computed(() => {
    const total = this.totalRegisters();
    const size = this.pageSize();
    return Math.floor(total / size) + (total % size > 0 ? 1 : 0);
  });

  protected readonly hasPrevPage = computed(() => this.current() > 1);
  protected readonly hasNextPage = computed(() => this.current() < this.totalPages());
  protected readonly from = computed(() => this.totalRegisters() || this.totalPages() * this.pageSize());
  protected readonly startFrom = computed(() => (this.totalRegisters() ? (this.current() - 1) * this.pageSize() + 1 : 0));
  protected readonly endFrom = computed(() => Math.min((this.current() - 1) * this.pageSize() + this.pageSize(), this.from()));

  optionChanged(event: Event | number): void {
    const nextPageSize = typeof event === 'number' ? event : Number((event.target as HTMLSelectElement | null)?.value ?? this.pageSize());

    if (this.disabled() || this.loading() || this.current() > this.totalPages()) {
      return;
    }

    this.onPageSizeChanged.emit({ page: this.current(), pageSize: nextPageSize });
  }

  nextPage(): void {
    if (this.disabled() || this.loading() || !this.hasNextPage()) {
      return;
    }
    this.onPageChanged.emit({ page: this.current() + 1, pageSize: this.pageSize() });
  }

  prevPage(): void {
    if (this.disabled() || this.loading() || !this.hasPrevPage()) {
      return;
    }
    this.onPageChanged.emit({ page: this.current() - 1, pageSize: this.pageSize() });
  }
}
